import type { UserRole } from '@/types/domain/app';

/**
 * Roles authorized to access the Hub application shell.
 * Includes organization-member (membership / empty shell) and module roles (apps).
 * The 'customer' role is explicitly excluded — customers use the Customer Portal.
 */
const HUB_AUTHORIZED_ROLE_LIST = [
  'system-admin',
  // FGA org membership marker — shell access only; no module app capability
  'organization-member',
  // Module roles (scope-agnostic)
  'customer-care-agent',
  'customer-care-admin',
  'customer-care-client-agent',
  'customer-care-client-admin',
  'finance-agent',
  'finance-admin',
  'data-ingestion-agent',
  'data-ingestion-admin',
  'analytics-agent',
  'analytics-admin',
  'campaigns-agent',
  'campaigns-admin',
  'ai-agents-agent',
  'ai-agents-admin',
  'finance-approver-1-admin',
  'finance-approver-2-admin',
  'refund-approver-1-admin',
  'refund-approver-2-admin',
  'settings-admin',
  'settings-viewer',
  'payout_approver_l1',
  'payout_approver_l2',
  // Legacy slugs retained during WorkOS role migration
  'operator-admin',
  'operator-ops',
  'operator-finance',
  'debt-type-analyst',
  'client-admin',
  'client-analyst',
  'client-viewer',
] satisfies ReadonlyArray<UserRole>;

const HUB_AUTHORIZED_ROLES: ReadonlySet<string> = new Set<string>(HUB_AUTHORIZED_ROLE_LIST);

/**
 * Normalize a role string to the canonical kebab-case form used by HUB_AUTHORIZED_ROLES.
 * Handles snake_case variants from E2E helpers, legacy sessions, and WorkOS SDK inconsistencies.
 */
export function normalizeRole(role: string): string {
  if (role === 'admin') return 'system-admin';
  // Must run before the `org_` prefix rewrite (organization_member ≠ org_*)
  if (role === 'organization_member' || role === 'organization-member') {
    return 'organization-member';
  }

  const kebabRoleSegments = ['system', 'operator', 'client', 'debt-type', 'org'];
  for (const prefix of kebabRoleSegments) {
    const snakePrefix = prefix.replace(/-/g, '_');
    if (role.startsWith(`${snakePrefix}_`)) {
      const suffix = role.slice(snakePrefix.length + 1);
      return `${prefix}-${suffix}`;
    }
  }

  return role;
}

function collectRoles(roleOrRoles: string | readonly string[] | null | undefined): string[] {
  if (roleOrRoles == null) return [];
  if (typeof roleOrRoles === 'string') {
    return roleOrRoles === '' ? [] : [roleOrRoles];
  }
  return roleOrRoles.filter((r) => r !== '');
}

/**
 * Check whether a WorkOS session is authorized for Hub shell access.
 * Requires a WorkOS organization context and at least one allowlisted role
 * (singular `role` or any entry in multi-role `roles[]`).
 */
export function isHubAuthorized(
  roleOrRoles: string | readonly string[] | null | undefined,
  organizationId: string | null | undefined
): boolean {
  if (!organizationId) return false;
  const roles = collectRoles(roleOrRoles);
  if (roles.length === 0) return false;
  return roles.some((role) => HUB_AUTHORIZED_ROLES.has(normalizeRole(role)));
}
